import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, Search, Grid3X3, List, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ExperienceCard from '../components/ExperienceCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExperiencesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [experiences, setExperiences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('rating');

  const category = searchParams.get('category') || 'all';
  const search = searchParams.get('search') || '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category && category !== 'all') params.append('category', category);
        if (search) params.append('search', search);

        const [expRes, catRes] = await Promise.all([
          axios.get(`${API}/experiences?${params}`),
          axios.get(`${API}/categories`)
        ]);

        let sorted = [...expRes.data];
        if (sortBy === 'rating') sorted.sort((a, b) => b.rating - a.rating);
        else if (sortBy === 'price_low') sorted.sort((a, b) => a.price - b.price);
        else if (sortBy === 'price_high') sorted.sort((a, b) => b.price - a.price);

        setExperiences(sorted);
        setCategories(catRes.data);
      } catch (err) {
        console.error('Failed to fetch:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category, search, sortBy]);

  const handleCategoryChange = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value === 'all') params.delete('category');
    else params.set('category', value);
    setSearchParams(params);
  };

  const handleSearchChange = (e) => {
    const params = new URLSearchParams(searchParams);
    if (e.target.value) params.set('search', e.target.value);
    else params.delete('search');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSortBy('rating');
  };

  const activeFilters = category !== 'all' || search;

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="experiences-page">
      {/* Header */}
      <section className="bg-white border-b border-[#2C3E36]/10">
        <div className="container-main py-12">
          <h1 className="font-['Avenir_Next'] text-4xl md:text-5xl text-[#1A1A1A] mb-4">
            {category !== 'all'
              ? categories.find(c => c.id === category)?.name || 'Upplevelser'
              : 'Alla upplevelser'}
          </h1>
          <p className="text-[#595959] text-lg">
            {search ? `Sökresultat för "${search}"` : 'Utforska vårt breda utbud av handplockade upplevelser'}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-20 z-30 bg-[#F9F9F7]/95 backdrop-blur-sm border-b border-[#8B6F4E]/10">
        <div className="container-main py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
              <Input
                type="text"
                placeholder="Sök upplevelser..."
                value={search}
                onChange={handleSearchChange}
                className="pl-10 h-11 rounded-full border-[#8B6F4E]/20"
                data-testid="filter-search"
              />
            </div>

            {/* Category */}
            <Select value={category} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-[180px] h-11 rounded-full border-[#8B6F4E]/20" data-testid="filter-category">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla kategorier</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] h-11 rounded-full border-[#8B6F4E]/20" data-testid="filter-sort">
                <SelectValue placeholder="Sortera" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Högst betyg</SelectItem>
                <SelectItem value="price_low">Lägst pris</SelectItem>
                <SelectItem value="price_high">Högst pris</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear filters */}
            {activeFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-[#B83A3A]"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Rensa filter
              </Button>
            )}

            {/* View toggle */}
            <div className="hidden md:flex items-center gap-1 ml-auto bg-white rounded-full p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-full transition-colors ${viewMode === 'grid' ? 'bg-[#8B6F4E] text-white' : 'text-[#8C8C8C]'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-full transition-colors ${viewMode === 'list' ? 'bg-[#8B6F4E] text-white' : 'text-[#8C8C8C]'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="section-padding">
        <div className="container-main">
          {loading ? (
            <div className={`grid ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : ''} gap-8`}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-xl overflow-hidden">
                  <div className="skeleton h-56" />
                  <div className="p-5 space-y-3">
                    <div className="skeleton h-4 w-20" />
                    <div className="skeleton h-6 w-3/4" />
                    <div className="skeleton h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : experiences.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#8C8C8C] text-lg mb-4">Inga upplevelser hittades</p>
              <Button onClick={clearFilters} variant="outline">
                Visa alla upplevelser
              </Button>
            </div>
          ) : (
            <>
              <p className="text-[#8C8C8C] mb-6">{experiences.length} upplevelser</p>
              <div className={`grid ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3 gap-8' : 'gap-4'}`}>
                {experiences.map((exp, i) => (
                  <ExperienceCard key={exp.id} experience={exp} index={i} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
